import { get, off, push, ref, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { getPathDatabaseByRole } from "./auth";
import { db } from "../lib/firebase";
import { LaunchError } from "../utils/errors";
import { deleteFolderAndUnlinkContents } from "./folderDeletion";
import {
  CONTENT_FOLDER_ROOT_ID,
  EDITOR_ASSET_FOLDER_CHILDREN_ROOT,
  EDITOR_ASSET_FOLDER_CONTENTS_ROOT,
  EDITOR_ASSET_FOLDERS_ROOT,
  EDITOR_ASSETS_LIST_ROOT,
} from "../utils/constants";
import {
  isDuplicateFolderName,
  normalizeFolderName,
  normalizeParentFolderID,
} from "../utils/contentFolderUtils";

const getAssetFoldersPath = ({ advertiserID }) =>
  `${EDITOR_ASSET_FOLDERS_ROOT}/${advertiserID}`;

const getAssetFolderPath = ({ advertiserID, folderID }) =>
  `${getAssetFoldersPath({ advertiserID })}/${folderID}`;

const getAssetFolderChildrenPath = ({ advertiserID, parentFolderID }) =>
  `${EDITOR_ASSET_FOLDER_CHILDREN_ROOT}/${advertiserID}/${parentFolderID}`;

const getAssetFolderContentIndexPath = ({
  advertiserID,
  folderID,
  contentID,
}) =>
  `${EDITOR_ASSET_FOLDER_CONTENTS_ROOT}/${advertiserID}/${folderID}/${contentID}`;

export const startListenerAssetFolders =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getAssetFoldersPath({ advertiserID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || {});
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerAssetFolders",
          tags: { action: "assetFolders" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerAssetFolders =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startCreateAssetFolder =
  ({ name, parentFolderID = CONTENT_FOLDER_ROOT_ID }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);
    const normalizedParentID = normalizeParentFolderID(parentFolderID);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const foldersPath = getAssetFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: normalizedParentID,
          name: normalizedName,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const folderID = push(ref(db, foldersPath)).key;
      const now = Date.now();
      const folder = {
        name: normalizedName,
        parentFolderID: normalizedParentID,
        creationTime: now,
        updatedTime: now,
      };

      await update(ref(db), {
        [getAssetFolderPath({ advertiserID, folderID })]: folder,
        [`${getAssetFolderChildrenPath({
          advertiserID,
          parentFolderID: normalizedParentID,
        })}/${folderID}`]: true,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          ...folder,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateAssetFolder",
          context: { name: normalizedName, parentFolderID: normalizedParentID },
          tags: { action: "assetFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startMoveAssetsToFolder =
  ({ contentIDs, folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || !folderID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      const targetFolderSnapshot = await get(
        ref(db, getAssetFolderPath({ advertiserID, folderID }))
      );
      if (!targetFolderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const contentSnapshots = await Promise.all(
        uniqueContentIDs.map(async (contentID) => {
          const snapshot = await get(
            ref(db, `${EDITOR_ASSETS_LIST_ROOT}/${advertiserID}/${contentID}`)
          );
          return {
            contentID,
            content: snapshot.exists() ? snapshot.val() : null,
          };
        })
      );

      const updates = {};
      const movedContentIDs = [];

      contentSnapshots.forEach(({ contentID, content }) => {
        if (!content) return;

        const previousFolderID = content.folderID;
        if (previousFolderID === folderID) return;

        if (previousFolderID) {
          updates[
            getAssetFolderContentIndexPath({
              advertiserID,
              folderID: previousFolderID,
              contentID,
            })
          ] = null;
        }

        updates[
          getAssetFolderContentIndexPath({
            advertiserID,
            folderID,
            contentID,
          })
        ] = true;
        updates[
          `${EDITOR_ASSETS_LIST_ROOT}/${advertiserID}/${contentID}/folderID`
        ] = folderID;
        movedContentIDs.push(contentID);
      });

      if (movedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: movedContentIDs,
          folderID,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startMoveAssetsToFolder",
          context: { contentIDs, folderID },
          tags: { action: "assetFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startUnassignAssetsFromFolder =
  ({ contentIDs }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      const contentSnapshots = await Promise.all(
        uniqueContentIDs.map(async (contentID) => {
          const snapshot = await get(
            ref(db, `${EDITOR_ASSETS_LIST_ROOT}/${advertiserID}/${contentID}`)
          );
          return {
            contentID,
            content: snapshot.exists() ? snapshot.val() : null,
          };
        })
      );

      const updates = {};
      const unassignedContentIDs = [];

      contentSnapshots.forEach(({ contentID, content }) => {
        if (!content?.folderID) return;

        updates[
          getAssetFolderContentIndexPath({
            advertiserID,
            folderID: content.folderID,
            contentID,
          })
        ] = null;
        updates[
          `${EDITOR_ASSETS_LIST_ROOT}/${advertiserID}/${contentID}/folderID`
        ] = null;
        unassignedContentIDs.push(contentID);
      });

      if (unassignedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: unassignedContentIDs,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUnassignAssetsFromFolder",
          context: { contentIDs },
          tags: { action: "assetFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRenameAssetFolder =
  ({ folderID, name }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !folderID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const folderSnapshot = await get(
        ref(db, getAssetFolderPath({ advertiserID, folderID }))
      );

      if (!folderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const currentFolder = folderSnapshot.val();
      const foldersPath = getAssetFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: currentFolder.parentFolderID,
          name: normalizedName,
          excludeFolderID: folderID,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const now = Date.now();
      await update(ref(db), {
        [`${getAssetFolderPath({ advertiserID, folderID })}/name`]:
          normalizedName,
        [`${getAssetFolderPath({ advertiserID, folderID })}/updatedTime`]: now,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          name: normalizedName,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRenameAssetFolder",
          context: { folderID, name: normalizedName },
          tags: { action: "assetFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startDeleteAssetFolder =
  ({ folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      return await deleteFolderAndUnlinkContents({
        advertiserID,
        folderID,
        foldersRoot: EDITOR_ASSET_FOLDERS_ROOT,
        childrenRoot: EDITOR_ASSET_FOLDER_CHILDREN_ROOT,
        contentsRoot: EDITOR_ASSET_FOLDER_CONTENTS_ROOT,
        getEntityPaths: (id) => [
          `${EDITOR_ASSETS_LIST_ROOT}/${advertiserID}/${id}`,
        ],
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteAssetFolder",
          context: { folderID },
          tags: { action: "assetFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
